import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
} from "recharts";
import {
  Users,
  Eye,
  MousePointerClick,
  ShoppingCart,
  TrendingUp,
  Loader2,
  RefreshCw,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const PALETTE = ["#2563eb", "#06b6d4", "#f59e0b", "#8b5cf6", "#10b981", "#f43f5e", "#0ea5e9", "#eab308", "#ec4899", "#14b8a6", "#6366f1", "#f97316"];
const PERIODS = [
  { key: "daily", label: "Day", note: "Last 30 days" },
  { key: "weekly", label: "Week", note: "Last 12 weeks" },
  { key: "monthly", label: "Month", note: "Last 12 months" },
  { key: "yearly", label: "Year", note: "Last 5 years" },
];

const fmtDay = (d) => {
  const dt = new Date(d + "T00:00:00");
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const StatCard = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div data-testid={testId} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg">
    <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-10" style={{ background: accent }} />
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl text-white shadow-sm" style={{ background: accent }}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
    </div>
    <p className="mt-4 font-display text-3xl font-extrabold text-slate-900">{value}</p>
    {sub != null && <p className="mt-1 text-xs font-semibold text-slate-500">{sub}</p>}
  </div>
);

const Panel = ({ title, subtitle, children, right, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-base font-extrabold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs font-semibold text-slate-500">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  boxShadow: "0 10px 30px -10px rgba(2,6,23,0.25)",
  fontSize: 12,
  fontWeight: 600,
};

export const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("daily");

  const [updatedAt, setUpdatedAt] = useState(null);

  const load = (manual = false) => {
    setLoading(true);
    const minDelay = new Promise((r) => setTimeout(r, manual ? 600 : 0));
    Promise.all([api.get("/admin/analytics", { params: { _: Date.now() } }), minDelay])
      .then(([r]) => {
        setData(r.data);
        setError("");
        setUpdatedAt(new Date());
        if (manual) toast.success("Analytics refreshed");
      })
      .catch((e) => (manual && data ? toast.error(formatApiError(e)) : setError(formatApiError(e))))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
  }, []);

  const periodData = useMemo(() => {
    if (!data) return [];
    const rows = data.series[period] || [];
    return rows.map((r) => ({ ...r, label: period === "daily" ? fmtDay(r.label) : r.label }));
  }, [data, period]);

  const dailyClicks = useMemo(() => {
    if (!data) return [];
    return data.series.daily.map((r) => ({ label: fmtDay(r.label), demo: r.demo, buy: r.buy }));
  }, [data]);

  if (loading && !data)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );

  if (error)
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700" data-testid="analytics-error">
        {error}
      </div>
    );

  const { visits, uniques, demo, buy, referrers, recent } = data;
  const activePeriod = PERIODS.find((p) => p.key === period);
  const conv = visits.all > 0 ? ((buy.all / visits.all) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-extrabold text-slate-900">Visitor analytics</h2>
          <p className="text-sm text-slate-600">Traffic sources, visits over time and button engagement.</p>
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && <span className="text-xs font-semibold text-slate-500" data-testid="analytics-updated-at">Updated {updatedAt.toLocaleTimeString()} · auto-updates every 30s</span>}
          <button
            onClick={() => load(true)}
            disabled={loading}
            data-testid="analytics-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-900 transition-colors hover:border-blue-600 hover:text-blue-600 disabled:cursor-wait disabled:opacity-70"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Visit stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="analytics-visit-cards">
        <StatCard testId="stat-visits-today" icon={Eye} label="Visits today" value={visits.today} sub={`${uniques.today} unique`} accent="#2563eb" />
        <StatCard testId="stat-visits-week" icon={TrendingUp} label="This week" value={visits.week} sub={`${uniques.week} unique`} accent="#06b6d4" />
        <StatCard testId="stat-visits-month" icon={Users} label="This month" value={visits.month} sub={`${uniques.month} unique`} accent="#8b5cf6" />
        <StatCard testId="stat-visits-year" icon={Globe} label="This year" value={visits.year} sub={`${uniques.year} unique`} accent="#10b981" />
      </div>

      {/* Engagement cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="analytics-engagement-cards">
        <StatCard testId="stat-demo-clicks" icon={MousePointerClick} label="Demo clicks" value={demo.all} sub={`${demo.today} today · ${demo.month} this month`} accent="#f59e0b" />
        <StatCard testId="stat-buy-clicks" icon={ShoppingCart} label="Buy Now clicks" value={buy.all} sub={`${buy.today} today · ${buy.month} this month`} accent="#f43f5e" />
        <StatCard testId="stat-total-visits" icon={Eye} label="Total visits" value={visits.all} sub={`${uniques.all} unique visitors`} accent="#0ea5e9" />
        <StatCard testId="stat-conversion" icon={TrendingUp} label="Buy conversion" value={`${conv}%`} sub="Buy clicks / total visits" accent="#6366f1" />
      </div>

      {/* Visits over time with period toggle */}
      <Panel
        testId="analytics-traffic-panel"
        title="Traffic over time"
        subtitle={activePeriod?.note}
        right={
          <div className="flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="analytics-period-toggle">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`analytics-period-${p.key}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${period === p.key ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:text-blue-600"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        }
      >
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {period === "daily" ? (
              <AreaChart data={periodData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.45} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
                  </linearGradient>
                  <linearGradient id="gUniques" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                <Area type="monotone" dataKey="visits" name="Visits" stroke="#2563eb" strokeWidth={2.5} fill="url(#gVisits)" />
                <Area type="monotone" dataKey="uniques" name="Unique" stroke="#06b6d4" strokeWidth={2.5} fill="url(#gUniques)" />
              </AreaChart>
            ) : (
              <BarChart data={periodData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="visits" name="Visits" radius={[6, 6, 0, 0]}>
                  {periodData.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Referrers */}
        <Panel testId="analytics-referrers-panel" title="Where visitors come from" subtitle="Top referring sites (all time)">
          {referrers.length === 0 ? (
            <p className="py-10 text-center text-sm font-semibold text-slate-400">No visits recorded yet.</p>
          ) : (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={referrers} layout="vertical" margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="source" width={120} tick={{ fontSize: 11, fill: "#334155", fontWeight: 600 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="visits" name="Visits" radius={[0, 6, 6, 0]}>
                    {referrers.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        {/* Demo vs Buy clicks */}
        <Panel testId="analytics-clicks-panel" title="Button engagement" subtitle="Demo vs Buy Now clicks (last 30 days)">
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyClicks} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                <Line type="monotone" dataKey="demo" name="Demo clicks" stroke="#f59e0b" strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="buy" name="Buy Now clicks" stroke="#f43f5e" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      {/* Recent visits */}
      <Panel testId="analytics-recent-panel" title="Recent visits" subtitle="Latest 15 page views">
        {recent.length === 0 ? (
          <p className="py-10 text-center text-sm font-semibold text-slate-400">No visits recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="analytics-recent-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="pb-2 pr-4 font-bold">When</th>
                  <th className="pb-2 pr-4 font-bold">Source</th>
                  <th className="pb-2 pr-4 font-bold">Page</th>
                  <th className="pb-2 font-bold">Visitor</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5 pr-4 font-semibold text-slate-700">{new Date(r.ts).toLocaleString()}</td>
                    <td className="py-2.5 pr-4">
                      <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700">{r.source}</span>
                    </td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-slate-600">{r.path}</td>
                    <td className="py-2.5 font-mono text-xs text-slate-400">{r.visitor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
};
